// Lightweight TE/EN i18n helper
import { useEffect, useState } from "react";

const LANG_KEY = "anagha_lang";

export const getLang = () => (typeof window !== "undefined" ? localStorage.getItem(LANG_KEY) || "te" : "te");
export const setLang = (l) => {
  localStorage.setItem(LANG_KEY, l);
  window.dispatchEvent(new Event("langchange"));
};

// React hook — re-renders the component when language changes
export const useLang = () => {
  const [lang, setL] = useState(getLang());
  useEffect(() => {
    const fn = () => setL(getLang());
    window.addEventListener("langchange", fn);
    return () => window.removeEventListener("langchange", fn);
  }, []);
  return lang;
};

// Comprehensive string dictionary
const D = {
  // common
  back:              { te: "తిరిగి వెళ్ళండి",            en: "Back" },
  back_short:        { te: "తిరిగి",                     en: "Back" },
  royal_court:       { te: "రాజ సభ",                     en: "Royal Court" },
  exit:              { te: "నిష్క్రమణ",                   en: "Exit" },
  live:              { te: "లైవ్",                        en: "Live" },
  projector:         { te: "ప్రొజెక్టర్",                 en: "Projector" },
  admin:             { te: "నిర్వాహకుడు",                en: "Admin" },

  // Welcome page
  welcome_eyebrow:   { te: "మొదటి పుట్టినరోజు · 🏰",      en: "First Birthday · 🏰" },
  welcome_script:    { te: "మా చిన్నారి... మా ప్రపంచం",   en: "Our little one... our world" },
  welcome_title:     { te: "అనఘ రాజ్యం",                  en: "Anagha's Kingdom" },
  welcome_sub:       { te: "అనఘ రాజ్యానికి స్వాగతం",      en: "Welcome to Anagha's Kingdom" },
  welcome_body:      {
    te: "ఈ రోజు మీరు మా యువరాణి అనఘ రాజ్యంలో ఒక ప్రత్యేక సభ్యులు కాబోతున్నారు. మీ ప్రేమ, ఆశీర్వాదాలు మరియు ఆనందంతో ఈ రాజ్యం మరింత అందంగా మారుతుంది.",
    en: "Today you become a special member of Princess Anagha's Kingdom. Your love and blessings make this kingdom more beautiful.",
  },
  cta_enter:         { te: "రాజ్యంలో ప్రవేశించండి",       en: "Enter the Kingdom" },
  cta_gallery:       { te: "నక్షత్ర ఆకాశం చూడండి",         en: "View the Sky of Stars" },
  cta_dance_floor:   { te: "రాజ్య నృత్య వేదిక",           en: "Royal Dance Floor" },
  princess_name:     { te: "యువరాణి అనఘ",                en: "Princess Anagha" },
  princess_sub:      { te: "మా చిన్నారి · ఒక సంవత్సరం",   en: "Our little one · turning one" },
  cake_finale:       { te: "కేక్ కట్ సమయంలో గ్రాండ్ ఫైనాలే", en: "Grand finale at cake cutting" },
  to_dance:          { te: "నృత్య వేదిక →",               en: "Dance Floor →" },

  // Register page — Journey Roadmap
  journey_eyebrow:   { te: "నేటి రాత్రి · రాజ యాత్ర",    en: "Tonight · Royal Journey" },
  journey_title:     { te: "నేటి రాత్రి రాజ యాత్ర",       en: "Tonight's Royal Journey" },
  journey_sub:       {
    te: "ఆరు మధురమైన అధ్యాయాలు — ఒక్కొక్కటీ ఒక చిన్ని జ్ఞాపకం. ఇప్పుడు మీరు రెండవ అధ్యాయంలో ఉన్నారు.",
    en: "Six sweet chapters — each one a little memory. You are now stepping into the second chapter.",
  },
  journey_now:       { te: "ఇప్పుడు ఇక్కడ ఉన్నారు",       en: "You are here now" },
  journey_done:      { te: "పూర్తయింది",                  en: "Completed" },
  journey_next:      { te: "త్వరలో",                      en: "Coming up" },
  journey_step1_t:   { te: "రాజ్యంలోకి ప్రవేశం",          en: "Enter the Kingdom" },
  journey_step1_s:   { te: "బంగారు ద్వారాలు మీ కోసం తెరుచుకున్నాయి", en: "The golden doors have opened for you" },
  journey_step2_t:   { te: "రాజ్య సభ్యులుగా చేరండి",     en: "Become a Kingdom Member" },
  journey_step2_s:   { te: "మీ పేరు, బంధం, ఫోటో ఇచ్చి రాజ హోదా పొందండి", en: "Share your name, bond & photo to receive your royal title" },
  journey_step3_t:   { te: "యువరాణి అనఘతో వేడుక",       en: "Celebrate with Princess Anagha" },
  journey_step3_s:   { te: "నృత్య వేదికపై మీ అవతారం అనఘ చుట్టూ నాట్యం చేస్తుంది", en: "Your avatar dances around Anagha on the royal floor" },
  journey_step4_t:   { te: "రాజ కేక్ ఉత్సవం",            en: "Royal Cake Ceremony" },
  journey_step4_s:   { te: "అనఘ మొదటి కేక్ కట్ — దీపాలు, ఆశీర్వాదాలు", en: "Anagha's first cake cut — lights, blessings, cheers" },
  journey_step5_t:   { te: "రాజ భోజన సభ",                en: "Royal Feast" },
  journey_step5_s:   { te: "రుచికరమైన విందు, అనఘ నుండి సందేశాలు", en: "A delightful feast, with messages from Anagha" },
  journey_step6_t:   { te: "రాజ జ్ఞాపిక తీసుకెళ్ళండి",     en: "Take Home a Royal Memory" },
  journey_step6_s:   { te: "మీ పోర్ట్రెయిట్, గౌరవ పత్రం మరియు అనఘ ప్రేమ", en: "Your portrait, certificate and Anagha's love" },

  // Royal Arrival — used on Reveal page (post-registration) and Dance Floor projector
  arrival_royal_family: { te: "రాజ కుటుంబం విచ్చేసారు!",   en: "The Royal Family has arrived!" },
  arrival_welcome:      { te: "అనఘ రాజ్యానికి స్వాగతం",     en: "Welcome to Anagha's Kingdom" },
  arrival_proclaim:     { te: "మహారాజా ఆజ్ఞ · నూతన సభ్యుడు రాజ్యంలో చేరారు",
                          en: "By royal decree · A new member has joined the kingdom" },
  arrival_next_chapter: { te: "ఇప్పుడు రాజ యాత్రలో ఉన్నారు",
                          en: "You are now on the royal journey" },

  // Auto-return-to-register countdown (Reveal page)
  rev_next_guest_in:    { te: "తదుపరి అతిథి కోసం సిద్ధమవుతోంది",
                          en: "Getting ready for the next guest" },
  rev_next_guest_now:   { te: "ఇప్పుడే మరో అతిథిని చేర్చండి",
                          en: "Add Next Guest Now" },
  rev_stay_here:        { te: "ఇక్కడే ఉండండి",
                          en: "Stay on this page" },
  rev_seconds:          { te: "సెకన్లు",                     en: "seconds" },

  // Register page
  reg_eyebrow:       { te: "అనఘ రాజ్యానికి స్వాగతం",     en: "Welcome to Anagha's Kingdom" },
  reg_title_a:       { te: "మీ",                          en: "Get your" },
  reg_title_b:       { te: "రాజ్య హోదా",                  en: "Royal Title" },
  reg_title_c:       { te: "పొందండి",                     en: "" },
  reg_subtitle:      {
    te: "మీరు అనఘకు ఎవరో చెప్పండి. మేము మీ ఫోటోను అనఘ నృత్య వేదికపై ప్రదర్శిస్తాము.",
    en: "Tell us who you are to Anagha. We'll add your photo to Anagha's live dance floor.",
  },
  reg_name_label:    { te: "మీ పేరు",                     en: "Your Name" },
  reg_name_ph:       { te: "ఉదా: రవి, గీత",              en: "e.g., Ravi, Geetha" },
  reg_rel_label:     { te: "అనఘతో మీ బంధం",              en: "Your Relation to Anagha" },
  reg_photo_label:   { te: "మీ ఫోటో (సెల్ఫీ)",            en: "Your Photo (Selfie)" },
  reg_upload:        { te: "ఫోటో అప్‌లోడ్ చేయండి",        en: "Upload Photo" },
  reg_upload_hint:   { te: "ముఖం స్పష్టంగా కనిపించే ఫోటో మంచిది", en: "A clear photo of your face works best" },
  reg_recrop:        { te: "✎ మళ్ళీ క్రాప్",              en: "✎ Re-crop" },
  reg_change:        { te: "✕ ఫోటో మార్చండి",             en: "✕ Change Photo" },
  reg_error_fields:  { te: "దయచేసి మీ పేరు, బంధం మరియు సెల్ఫీ ఇవ్వండి.", en: "Please share your name, royal title, and a selfie." },
  reg_submit:        { te: "రాజ్యంలో చేరండి",             en: "Join the Kingdom" },
  reg_submitting:    { te: "మీ హోదా తయారవుతోంది...",      en: "Preparing your title..." },

  // Reveal
  rev_loading_1:     { te: "మీ రాజ్య హోదా సిద్ధమవుతోంది...", en: "Preparing your royal title..." },
  rev_loading_2:     { te: "మీ బిరుదు ఎంచుతున్నాం...",    en: "Choosing your honour..." },
  rev_loading_3:     { te: "అనఘ మీకు సందేశం వ్రాస్తోంది...", en: "Anagha is writing a message for you..." },
  rev_loading_body:  { te: "మీ గుర్తు ప్రేమతో తయారవుతోంది. ఒక నిమిషం వేచి ఉండండి.", en: "Your keepsake is being made with love. Just a moment." },
  rev_failed:        { te: "పోర్ట్రెయిట్ తయారు కాలేదు.",  en: "Portrait could not be made." },
  rev_retry:         { te: "మళ్ళీ ప్రయత్నించండి",         en: "Try Again" },
  rev_certificate:   { te: "రాజ్య గౌరవ పత్రం",            en: "Royal Certificate" },
  rev_message_from:  { te: "అనఘ నుండి సందేశం",           en: "Message from Anagha" },
  rev_to_dance:      { te: "రాజ్య నృత్య వేదికలోకి",       en: "Go to Dance Floor" },
  rev_add_more:      { te: "మరో అతిథిని చేర్చండి",        en: "Add Another Guest" },
  rev_share_label:   { te: "మీ పోర్ట్రెయిట్ షేర్ చేయండి",  en: "Share your portrait" },

  // Gallery
  gal_breadcrumb:    { te: "అనఘ నక్షత్ర ఆకాశం",            en: "Anagha's Sky of Stars" },
  gal_title_a:       { te: "అనఘ",                         en: "Anagha's" },
  gal_title_b:       { te: "నక్షత్ర ఆకాశం",                en: "Sky of Stars" },
  gal_count_a:       { te: "నక్షత్రాలు మెరుస్తున్నాయి.",     en: "stars are shining." },
  gal_refresh:       { te: "ప్రతి కొద్ది సెకన్లకు అప్‌డేట్ అవుతుంది.", en: "Updates every few seconds." },
  gal_empty:         { te: "రాజ్యం మొదటి అతిథి కోసం వేచి ఉంది.", en: "The kingdom awaits its first guest." },
  gal_first_btn:     { te: "మొదటి పోర్ట్రెయిట్ చేయండి",  en: "Make the First Portrait" },
  gal_finale:        { te: "గ్రాండ్ ఫైనాలే",               en: "Grand Finale" },
  gal_live_refresh:  { te: "లైవ్ · ప్రతి 5 సెకన్లకు అప్‌డేట్", en: "live updates · auto-refresh every 5s" },

  // Dance floor
  df_kingdom:        { te: "అనఘ రాజ్యం",                  en: "Anagha's Kingdom" },
  df_guests:         { te: "మంది",                        en: "guests" },
  df_princess:       { te: "యువరాణి అనఘ",                en: "Princess Anagha" },

  // Royal Banquet (Dance Floor header)
  banquet_eyebrow:   { te: "రాజ విందు మందిరం",           en: "The Dining Hall" },
  banquet_title:     { te: "రాజ విందు",                   en: "The Royal Banquet" },
  banquet_sub:       {
    te: "మా చిన్నారి అనఘ గురించి కొన్ని ముద్దుల నిజాలు — ప్రతి బల్ల మీద ఒక చిన్ని కథ.",
    en: "Little truths about our tiny Anagha — one sweet story on every table.",
  },
  banquet_tent:      { te: "బల్ల టెంట్",                  en: "Table Tent" },
  banquet_fact1_l:   { te: "అత్యంత ప్రియమైన నవ్వు",       en: "Favourite Smile" },
  banquet_fact1_v:   {
    te: "అమ్మ ముఖం చూడగానే బుగ్గల నిండా వెలిగే పాల నవ్వు",
    en: "The milky grin that lights up the room when she sees Amma",
  },
  banquet_fact2_l:   { te: "అత్యంత ప్రియమైన బొమ్మ",       en: "Favourite Toy" },
  banquet_fact2_v:   {
    te: "గులాబీ చిన్ని ఏనుగు · పేరు 'పింకూ'",
    en: "A tiny pink elephant lovingly called 'Pinku'",
  },
  banquet_fact3_l:   { te: "అత్యంత ప్రియమైన పాట",         en: "Favourite Song" },
  banquet_fact3_v:   {
    te: "'చందమామ రావే జాబిల్లి రావే' — వినగానే నృత్యం మొదలు",
    en: "'Chandamama Raave' — the moment it plays, she starts dancing",
  },
  banquet_fact4_l:   { te: "అత్యంత ప్రియమైన మాట",         en: "Favourite Word" },
  banquet_fact4_v:   {
    te: "'తా-తా' అని చేయి ఊపుతూ ప్రతి ఒక్కరికీ వీడ్కోలు",
    en: "'Ta-taa!' — waving to everyone with a tiny hand",
  },
  banquet_fact5_l:   { te: "అత్యంత ప్రియమైన ఆహారం",     en: "Favourite Food" },
  banquet_fact5_v:   {
    te: "అరటిపండు మెత్తగా చిదిమిన ముద్ద — రెండో బెంచీ కూడా ఇష్టం",
    en: "Mashed banana — with a hopeful reach for a second helping",
  },
  banquet_fact6_l:   { te: "అత్యంత ప్రియమైన ఆట",         en: "Favourite Game" },
  banquet_fact6_v:   {
    te: "'ఏదీ · ఇదిగో!' పీక-బూ — నవ్వుల వర్షం",
    en: "Peek-a-boo — endless giggles guaranteed",
  },

  df_waiting:        { te: "మొదటి అతిథి కోసం వేచి ఉన్నాం", en: "Waiting for the first guest" },
  df_qr_hint:        { te: "QR స్కాన్ చేసి ఫోటో అప్‌లోడ్ చేయండి", en: "Scan the QR code and upload your photo" },
  df_arrival:        { te: "రాజ్యంలో చేరారు ✨",           en: "joined the Kingdom ✨" },
  df_enable_sound:   { te: "🔊 సంగీతం ఆన్ చేయండి",         en: "🔊 Tap for sound" },
  df_milestone_100:  { te: "చారిత్రాత్మక వేడుక",          en: "Legendary Celebration" },
  df_milestone_75:   { te: "ఆనంద రాజ్యం",                  en: "Kingdom of Joy" },
  df_milestone_50:   { te: "గ్రాండ్ ఫెస్టివల్",            en: "Grand Kingdom Festival" },
  df_milestone_25:   { te: "రాజ వేడుక",                   en: "Royal Celebration" },
  df_milestone_10:   { te: "రాజ్య సభ",                    en: "Kingdom Gathering" },
  df_milestone_0:    { te: "రాజ్యం ప్రారంభమవుతోంది",      en: "Kingdom beginning to fill" },

  // Projector
  proj_new_member:   { te: "కొత్త సభ్యుడు రాజ్యంలో చేరారు", en: "New member joined the kingdom" },
  proj_rotates:      { te: "లైవ్ · ప్రతి 30 సెకన్లకు మారుతుంది", en: "Live · rotates every 30 seconds" },
  proj_waiting:      { te: "మొదటి అతిథి కోసం వేచి ఉన్నాం", en: "Waiting for the first guest" },

  // Finale
  fin_letter_to:     { te: "ప్రేమ లేఖ —",                  en: "a love letter to" },
  fin_title_a:       { te: "నా ప్రపంచాన్ని",               en: "The People Who" },
  fin_title_b:       { te: "నింపిన వారు",                  en: "Make Up My World" },
  fin_message:       {
    te: "నా మొదటి పుట్టినరోజు వేడుకలో పాల్గొన్న ప్రతి ఒక్కరికీ, నా ప్రపంచాన్ని ప్రేమతో నింపినందుకు ధన్యవాదాలు.",
    en: "To every one of you who joined my first birthday celebration — thank you for filling my world with love.",
  },
  fin_replay:        { te: "✦ ఫైనాలే మళ్ళీ చూడండి",        en: "✦ Replay the Finale" },
  fin_turning_one:   { te: "ఒక సంవత్సరం పూర్తి",            en: "turning one" },
};

export const t = (key, lang) => {
  const l = lang || getLang();
  const entry = D[key];
  if (!entry) return key;
  // Use language-specific string even if empty (avoid falsy fallback to TE)
  if (entry[l] !== undefined) return entry[l];
  return entry.te !== undefined ? entry.te : key;
};
